import { Box, CircularProgress, Container, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useCheckSessionQuery } from '../../services/api/userService';
import { captureError } from '../../services/monitoring/sentry';
import { userLoggedIn } from '../../state/reducers/root';
import FinchLogo from '../base/FinchLogo';

const AuthCallback: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data, error, isLoading } = useCheckSessionQuery();

  useEffect(() => {
    if (data?.is_valid && data.user) {
      dispatch(userLoggedIn(data.user));
      navigate('/', { replace: true });
    } else if (error) {
      captureError(error, { tags: { feature: 'google-sso-callback' } });
      const errorParam = searchParams.get('error');
      if (errorParam) {
        setErrorMessage(
          'Google sign-in was cancelled or failed. Please try again.',
        );
      } else {
        setErrorMessage(
          'Unable to verify your session. Please try logging in again.',
        );
      }
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    }
  }, [data, error, dispatch, navigate, searchParams]);

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 3,
        }}
      >
        <FinchLogo width="160px" />
        {isLoading && !errorMessage && (
          <>
            <CircularProgress size={32} />
            <Typography variant="body1" color="text.secondary">
              Completing sign in...
            </Typography>
          </>
        )}
        {errorMessage && (
          <Typography variant="body1" color="error">
            {errorMessage}
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default AuthCallback;
